import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Cube, ArrowLeft, FloppyDisk, Plus, Trash, ListDashes, Package,
  Ruler, Image, MagnifyingGlass
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_CUSTOM = 21;

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-6 mb-5">
      <h2 className="text-sm font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
        <Icon size={18} className="text-[#7d64ff]" /> {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, required, children, span = 1, hint }) {
  return (
    <div className={span === 2 ? 'md:col-span-2' : span === 3 ? 'md:col-span-3' : ''}>
      <Label className="text-xs font-semibold text-[#6b6b8a]">{label}{required && ' *'}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[10px] text-[#9c9db7] mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer px-3 py-2.5 border border-[#e5e5ea] rounded-lg hover:border-[#7d64ff]/30 transition-colors">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 rounded accent-[#7d64ff]" data-testid={testId} />
      <span className="text-sm text-[#3c3c64]">{label}</span>
    </label>
  );
}

function SwitchToggle({ label, sublabel, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border border-[#e5e5ea] rounded-lg hover:border-[#7d64ff]/30 transition-colors">
      <div>
        <p className="text-sm font-medium text-[#3c3c64]">{label}</p>
        {sublabel && <p className="text-[10px] text-[#9c9db7]">{sublabel}</p>}
      </div>
      <button type="button" onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[#7d64ff]' : 'bg-[#d1d1d6]'}`} data-testid={testId}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isNew = !productId || productId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [variants, setVariants] = useState([]);
  const [variantForm, setVariantForm] = useState({ sku: '', size: '', color: '', material: '', price: '', mrp: '', stock: '' });
  const [bundleChildren, setBundleChildren] = useState([]);
  const [bundleForm, setBundleForm] = useState({ child_sku: '', quantity: 1 });
  const [bundleAdding, setBundleAdding] = useState(false);
  const [skuPickerOpen, setSkuPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerQty, setPickerQty] = useState(1);

  const emptyForm = {
    sku: '', name: '', description: '', long_description: '', product_type: 'Normal',
    bundle_type: 'Virtual',
    category: '', brand: '', cost_price: '', price: '', mrp: '', hsn_code: '',
    size: '', color: '', pattern: '', material: '',
    weight: '', gross_weight: '', height: '', breadth: '',
    images: ['', '', '', '', '', '', '', '', '', ''],
    product_video: '',
    custom_attributes: {},
    is_active: true, is_hazardous: false, is_saleable: true,
    is_stockable: true, is_refurbished: false, is_purchaseable: true,
  };
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    axios.get(`${API}/api/categories`, { withCredentials: true }).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/api/brands`, { withCredentials: true }).then(r => setBrandsList(r.data)).catch(() => {});
    if (!isNew) fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`${API}/api/products/${productId}`, { withCredentials: true });
      const imgs = data.images || [];
      while (imgs.length < 10) imgs.push('');
      setForm({
        sku: data.sku || '', name: data.name || '', description: data.description || '',
        long_description: data.long_description || '', product_type: data.product_type || 'Normal',
        bundle_type: data.bundle_type || 'Virtual',
        category: data.category || '', brand: data.brand || '',
        cost_price: data.cost_price?.toString() || '', price: data.price?.toString() || '',
        mrp: data.mrp?.toString() || '', hsn_code: data.hsn_code || '',
        size: data.size || '', color: data.color || '', pattern: data.pattern || '',
        material: data.material || '',
        weight: data.weight?.toString() || '', gross_weight: data.gross_weight?.toString() || '',
        height: data.height?.toString() || '', breadth: data.breadth?.toString() || '',
        images: imgs,
        product_video: data.product_video || '',
        custom_attributes: data.custom_attributes || {},
        is_active: data.is_active ?? true, is_hazardous: data.is_hazardous ?? false,
        is_saleable: data.is_saleable ?? true, is_stockable: data.is_stockable ?? true,
        is_refurbished: data.is_refurbished ?? false, is_purchaseable: data.is_purchaseable ?? true,
      });
      try {
        const vRes = await axios.get(`${API}/api/products/${productId}/variants`, { withCredentials: true });
        setVariants(vRes.data || []);
      } catch { /* no variants */ }
      try {
        const bRes = await axios.get(`${API}/api/products/${productId}/bundle-children`, { withCredentials: true });
        setBundleChildren(bRes.data?.children || []);
      } catch { /* no bundle children */ }
    } catch {
      toast.error('Failed to load product');
      navigate('/products');
    } finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.sku || !form.name || !form.category || !form.price || !form.mrp) {
      toast.error('Please fill all required fields (SKU, Name, Category, Price, MRP)'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        mrp: parseFloat(form.mrp),
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        weight: form.weight ? parseFloat(form.weight) : null,
        gross_weight: form.gross_weight ? parseFloat(form.gross_weight) : null,
        height: form.height ? parseFloat(form.height) : null,
        breadth: form.breadth ? parseFloat(form.breadth) : null,
        images: form.images.filter(Boolean),
      };
      if (isNew) {
        await axios.post(`${API}/api/products`, payload, { withCredentials: true });
        toast.success('Product created');
      } else {
        await axios.patch(`${API}/api/products/${productId}`, payload, { withCredentials: true });
        toast.success('Product updated');
      }
      navigate('/products');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setImage = (i, val) => setForm(f => ({ ...f, images: f.images.map((img, idx) => idx === i ? val : img) }));
  const setCustom = (key, val) => setForm(f => ({ ...f, custom_attributes: { ...f.custom_attributes, [key]: val } }));
  const removeCustom = (key) => setForm(f => {
    const ca = { ...f.custom_attributes };
    delete ca[key];
    return { ...f, custom_attributes: ca };
  });
  const [newAttrKey, setNewAttrKey] = useState('');

  const handleAddVariant = async () => {
    if (!variantForm.size && !variantForm.color && !variantForm.material) return;
    try {
      const data = {
        sku: variantForm.sku || `${form.sku}-V${variants.length + 1}`,
        attributes: {},
        price: parseFloat(variantForm.price) || parseFloat(form.price) || 0,
        mrp: parseFloat(variantForm.mrp) || parseFloat(form.mrp) || 0,
        stock: parseInt(variantForm.stock) || 0
      };
      if (variantForm.size) data.attributes.size = variantForm.size;
      if (variantForm.color) data.attributes.color = variantForm.color;
      if (variantForm.material) data.attributes.material = variantForm.material;
      await axios.post(`${API}/api/products/${productId}/variants`, data, { withCredentials: true });
      toast.success('Variant added');
      setVariantForm({ sku: '', size: '', color: '', material: '', price: '', mrp: '', stock: '' });
      const vRes = await axios.get(`${API}/api/products/${productId}/variants`, { withCredentials: true });
      setVariants(vRes.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add variant'); }
  };

  const handleDeleteVariant = async (vid) => {
    if (!window.confirm('Delete this variant?')) return;
    try {
      await axios.delete(`${API}/api/products/${productId}/variants/${vid}`, { withCredentials: true });
      toast.success('Variant deleted');
      setVariants(v => v.filter(x => x.variant_id !== vid));
    } catch { toast.error('Failed to delete'); }
  };

  const handleAddBundleChild = async (childProductId, childSku, qty) => {
    setBundleAdding(true);
    try {
      const { data } = await axios.post(`${API}/api/products/${productId}/bundle-children`, {
        child_product_id: childProductId,
        child_sku: childSku,
        quantity: qty || 1
      }, { withCredentials: true });
      setBundleChildren(prev => [...prev, data]);
      toast.success(`${childSku} linked to bundle`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add child SKU'); }
    finally { setBundleAdding(false); }
  };

  const searchNormalSkus = async (search = '', page = 1) => {
    setPickerLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/products/search/normal-skus?search=${encodeURIComponent(search)}&page=${page}&limit=10`, { withCredentials: true });
      // Filter out already-linked children
      const linkedIds = new Set(bundleChildren.map(c => c.child_product_id));
      setPickerResults((data.products || []).filter(p => !linkedIds.has(p.product_id)));
      setPickerTotal(data.total || 0);
      setPickerPage(page);
    } catch { setPickerResults([]); }
    finally { setPickerLoading(false); }
  };

  const openSkuPicker = () => {
    setSkuPickerOpen(true);
    setPickerSearch('');
    setPickerQty(1);
    searchNormalSkus('', 1);
  };

  const handleUpdateBundleQty = async (mappingId, newQty) => {
    try {
      await axios.patch(`${API}/api/products/${productId}/bundle-children/${mappingId}`, { quantity: newQty }, { withCredentials: true });
      setBundleChildren(prev => prev.map(c => c.mapping_id === mappingId ? { ...c, quantity: newQty } : c));
    } catch { toast.error('Failed to update quantity'); }
  };

  const handleRemoveBundleChild = async (mappingId) => {
    if (!window.confirm('Remove this child SKU from the bundle?')) return;
    try {
      await axios.delete(`${API}/api/products/${productId}/bundle-children/${mappingId}`, { withCredentials: true });
      setBundleChildren(prev => prev.filter(c => c.mapping_id !== mappingId));
      toast.success('Child SKU removed');
    } catch { toast.error('Failed to remove'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
  const customEntries = Object.entries(form.custom_attributes || {});

  if (loading) return (
    <DashboardLayout>
      <div className="space-y-5 pt-2">
        <div className="h-10 skeleton rounded-xl w-56" />
        <div className="h-52 skeleton rounded-xl" />
        <div className="h-36 skeleton rounded-xl" />
        <div className="h-36 skeleton rounded-xl" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout
      title={isNew ? 'Create Product' : `Edit: ${form.name || form.sku}`}
      subtitle={isNew ? 'Add a new SKU to your catalog' : `SKU: ${form.sku}`}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/products')} data-testid="back-to-products">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="save-product-btn">
            <FloppyDisk size={16} className="mr-1.5" /> {saving ? 'Saving...' : isNew ? 'Create Product' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSave} data-testid="product-detail-page">

        {/* ===== BASIC INFORMATION (merged: Basic + Classification + Attributes + Flags) ===== */}
        <Section icon={Cube} title="Basic Information">
          {/* Row 1: SKU, Name, Type, Bundle Type */}
          <div className={`grid grid-cols-1 gap-5 ${form.product_type === 'Bundled Article' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <Field label="SKU Code" required hint={!isNew ? 'Cannot be changed after creation' : undefined}>
              <Input value={form.sku} onChange={e => setField('sku', e.target.value)} disabled={!isNew} placeholder="e.g. SKU12345" data-testid="product-sku" />
            </Field>
            <Field label="SKU Description" required>
              <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Short product name" data-testid="product-name" />
            </Field>
            <Field label="Type">
              <Select value={form.product_type || "Normal"} onValueChange={v => setField('product_type', v)}>
                <SelectTrigger data-testid="product-type"><SelectValue placeholder="Normal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Bundled Article">Bundled Article</SelectItem>
                  <SelectItem value="Style">Style</SelectItem>
                  <SelectItem value="Variant">Variant</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.product_type === 'Bundled Article' && (
              <Field label="Bundle Type" required>
                <Select value={form.bundle_type || "Virtual"} onValueChange={v => setField('bundle_type', v)}>
                  <SelectTrigger data-testid="product-bundle-type"><SelectValue placeholder="Virtual" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Virtual">Virtual</SelectItem>
                    <SelectItem value="Physical">Physical</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          {/* Row 2: Long Description */}
          <div className="mt-5">
            <Field label="SKU Long Description">
              <Textarea value={form.long_description || form.description} onChange={e => { setField('long_description', e.target.value); setField('description', e.target.value); }} rows={3} placeholder="Detailed product description..." data-testid="product-description" />
            </Field>
          </div>

          {/* Row 3: Category, Brand + Active toggle */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
            <Field label="Category" required>
              <Select value={form.category} onValueChange={v => setField('category', v)}>
                <SelectTrigger data-testid="product-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.filter((c, i, a) => a.findIndex(x => x.name === c.name) === i).map(cat => (
                    <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Brand">
              <Select value={form.brand || "none"} onValueChange={v => setField('brand', v === "none" ? "" : v)}>
                <SelectTrigger data-testid="product-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No brand</SelectItem>
                  {brandsList.map(b => <SelectItem key={b.brand_id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="flex items-end">
              <SwitchToggle label="Active" sublabel={form.is_active ? 'SKU is live' : 'SKU is inactive'} checked={form.is_active} onChange={v => setField('is_active', v)} testId="product-active" />
            </div>
          </div>

          {/* Row 4: Size, Color, Pattern, Material */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-5">
            <Field label="Size">
              <Input value={form.size} onChange={e => setField('size', e.target.value)} placeholder="e.g. L, 32, N/A" data-testid="product-size" />
            </Field>
            <Field label="Color">
              <Input value={form.color} onChange={e => setField('color', e.target.value)} placeholder="e.g. Blue, Black" data-testid="product-color" />
            </Field>
            <Field label="Pattern">
              <Input value={form.pattern} onChange={e => setField('pattern', e.target.value)} placeholder="e.g. Solid, Striped" data-testid="product-pattern" />
            </Field>
            <Field label="Material">
              <Input value={form.material} onChange={e => setField('material', e.target.value)} placeholder="e.g. Cotton, Ceramic" data-testid="product-material" />
            </Field>
          </div>

          {/* Row 5: Flags */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
            <Toggle label="Saleable" checked={form.is_saleable} onChange={v => setField('is_saleable', v)} testId="product-saleable" />
            <Toggle label="Stockable" checked={form.is_stockable} onChange={v => setField('is_stockable', v)} testId="product-stockable" />
            <Toggle label="Purchaseable" checked={form.is_purchaseable} onChange={v => setField('is_purchaseable', v)} testId="product-purchaseable" />
            <Toggle label="Refurbished" checked={form.is_refurbished} onChange={v => setField('is_refurbished', v)} testId="product-refurbished" />
            <Toggle label="Hazardous" checked={form.is_hazardous} onChange={v => setField('is_hazardous', v)} testId="product-hazardous" />
          </div>
        </Section>

        {/* ===== BUNDLE CHILDREN (only for Bundled Article type) ===== */}
        {form.product_type === 'Bundled Article' && !isNew && (
          <Section icon={Package} title={`Bundle Composition (${bundleChildren.length} child SKUs)`}>
            {bundleChildren.length > 0 && (
              <div className="border border-[#e5e5ea] rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm" data-testid="bundle-children-table">
                  <thead><tr className="border-b border-[#e5e5ea] bg-[#f9f9fb]">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Child SKU</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Product Name</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Unit Price</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Quantity</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Line Total</th>
                    <th className="px-4 py-2.5"></th>
                  </tr></thead>
                  <tbody>
                    {bundleChildren.map(c => (
                      <tr key={c.mapping_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]" data-testid={`bundle-child-${c.mapping_id}`}>
                        <td className="px-4 py-2.5 font-mono text-xs font-medium text-[#7d64ff]">{c.child_sku}</td>
                        <td className="px-4 py-2.5 text-[#3c3c64]">{c.child_name || '—'}</td>
                        <td className="px-4 py-2.5">{formatCurrency(c.child_price)}</td>
                        <td className="px-4 py-2.5">
                          <Input
                            type="number" min="1" value={c.quantity}
                            onChange={e => handleUpdateBundleQty(c.mapping_id, parseInt(e.target.value) || 1)}
                            className="w-20 h-8 text-sm text-center"
                            data-testid={`bundle-qty-${c.mapping_id}`}
                          />
                        </td>
                        <td className="px-4 py-2.5 font-semibold">{formatCurrency((c.child_price || 0) * (c.quantity || 1))}</td>
                        <td className="px-4 py-2.5">
                          <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => handleRemoveBundleChild(c.mapping_id)} data-testid={`remove-bundle-child-${c.mapping_id}`}>
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-[#f9f9fb]">
                      <td colSpan={4} className="px-4 py-2.5 text-right text-xs font-semibold text-[#6b6b8a]">Bundle Total</td>
                      <td className="px-4 py-2.5 font-bold text-[#7d64ff]">{formatCurrency(bundleChildren.reduce((sum, c) => sum + (c.child_price || 0) * (c.quantity || 1), 0))}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center gap-3" data-testid="add-bundle-child-form">
              <Button type="button" onClick={openSkuPicker} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-sm" data-testid="open-sku-picker-btn">
                <Plus size={14} className="mr-1.5" /> Add Child SKU
              </Button>
              <span className="text-xs text-[#9c9db7]">Search and select from active Normal SKUs</span>
            </div>
          </Section>
        )}

        {form.product_type === 'Bundled Article' && isNew && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-700" data-testid="bundle-save-first-notice">
            <strong>Bundle Composition:</strong> Save this product first, then you can link child SKUs to it.
          </div>
        )}

        {/* SKU Picker Popup */}
        <Dialog open={skuPickerOpen} onOpenChange={setSkuPickerOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="sku-picker-dialog">
            <DialogHeader>
              <DialogTitle>Select Child SKU</DialogTitle>
            </DialogHeader>
            <div className="relative mb-3">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" />
              <Input
                value={pickerSearch}
                onChange={e => { setPickerSearch(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchNormalSkus(pickerSearch, 1); } }}
                placeholder="Search by SKU Code or Product Name..."
                className="pl-9"
                data-testid="sku-picker-search"
                autoFocus
              />
              <Button type="button" variant="outline" size="sm" onClick={() => searchNormalSkus(pickerSearch, 1)} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 text-xs" data-testid="sku-picker-search-btn">Search</Button>
            </div>
            <div className="flex-1 overflow-y-auto border border-[#e5e5ea] rounded-lg">
              {pickerLoading ? (
                <div className="p-8 text-center text-[#9c9db7] text-sm">Searching...</div>
              ) : pickerResults.length === 0 ? (
                <div className="p-8 text-center text-[#9c9db7] text-sm">No active Normal SKUs found</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#e5e5ea] bg-[#f9f9fb] sticky top-0">
                    <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-[#9c9db7]">SKU</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-[#9c9db7]">Name</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-[#9c9db7]">Category</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-[#9c9db7]">Price</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-[#9c9db7]">Qty</th>
                    <th className="px-4 py-2"></th>
                  </tr></thead>
                  <tbody>
                    {pickerResults.map(p => (
                      <tr key={p.product_id} className="border-b border-[#f0f0f0] hover:bg-[#f8f7ff] transition-colors" data-testid={`picker-row-${p.product_id}`}>
                        <td className="px-4 py-2.5 font-mono text-xs font-medium text-[#7d64ff]">{p.sku}</td>
                        <td className="px-4 py-2.5 text-[#3c3c64]">{p.name}</td>
                        <td className="px-4 py-2.5 text-[#9c9db7] text-xs">{p.category}</td>
                        <td className="px-4 py-2.5">{formatCurrency(p.price)}</td>
                        <td className="px-4 py-2.5">
                          <Input type="number" min="1" defaultValue={1} className="w-16 h-7 text-xs text-center" data-testid={`picker-qty-${p.product_id}`}
                            onChange={e => { /* store on the row */ p._qty = parseInt(e.target.value) || 1; }} />
                        </td>
                        <td className="px-4 py-2.5">
                          <Button type="button" size="sm" className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-xs h-7 px-3" disabled={bundleAdding}
                            onClick={() => { handleAddBundleChild(p.product_id, p.sku, p._qty || 1); setPickerResults(prev => prev.filter(x => x.product_id !== p.product_id)); }}
                            data-testid={`picker-add-${p.product_id}`}>
                            <Plus size={12} className="mr-1" /> Add
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {pickerTotal > 10 && (
              <div className="flex items-center justify-between pt-3 border-t border-[#e5e5ea]">
                <span className="text-xs text-[#9c9db7]">{pickerTotal} SKUs found</span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pickerPage <= 1} onClick={() => searchNormalSkus(pickerSearch, pickerPage - 1)}>Prev</Button>
                  <span className="text-xs text-[#6b6b8a] py-1.5">Page {pickerPage}</span>
                  <Button type="button" variant="outline" size="sm" disabled={pickerResults.length < 10} onClick={() => searchNormalSkus(pickerSearch, pickerPage + 1)}>Next</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== PRICING & TAXATION ===== */}
        <Section icon={Package} title="Pricing & Taxation">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Field label="Cost Price">
              <Input type="number" step="0.01" value={form.cost_price} onChange={e => setField('cost_price', e.target.value)} placeholder="0.00" data-testid="product-cost-price" />
            </Field>
            <Field label="MRP" required>
              <Input type="number" step="0.01" value={form.mrp} onChange={e => setField('mrp', e.target.value)} placeholder="0.00" data-testid="product-mrp" />
            </Field>
            <Field label="Selling Price" required>
              <Input type="number" step="0.01" value={form.price} onChange={e => setField('price', e.target.value)} placeholder="0.00" data-testid="product-price" />
            </Field>
            <Field label="HSN Code">
              <Input value={form.hsn_code} onChange={e => setField('hsn_code', e.target.value)} placeholder="e.g. 61093010" className="font-mono" data-testid="product-hsn" />
            </Field>
          </div>
          {form.price && form.mrp && parseFloat(form.mrp) > parseFloat(form.price) && (
            <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 font-medium">
              Discount: {Math.round((1 - parseFloat(form.price) / parseFloat(form.mrp)) * 100)}% off MRP
              {form.cost_price && parseFloat(form.cost_price) > 0 && (
                <span className="ml-4">Margin: {Math.round(((parseFloat(form.price) - parseFloat(form.cost_price)) / parseFloat(form.price)) * 100)}%</span>
              )}
            </div>
          )}
        </Section>

        {/* ===== PHYSICAL SPECIFICATIONS ===== */}
        <Section icon={Ruler} title="Physical Specifications">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Field label="Height (cm)">
              <Input type="number" step="0.1" value={form.height} onChange={e => setField('height', e.target.value)} placeholder="0" data-testid="product-height" />
            </Field>
            <Field label="Breadth (cm)">
              <Input type="number" step="0.1" value={form.breadth} onChange={e => setField('breadth', e.target.value)} placeholder="0" data-testid="product-breadth" />
            </Field>
            <Field label="Weight (kg)">
              <Input type="number" step="0.01" value={form.weight} onChange={e => setField('weight', e.target.value)} placeholder="0.0" data-testid="product-weight" />
            </Field>
            <Field label="Gross Weight (kg)">
              <Input type="number" step="0.01" value={form.gross_weight} onChange={e => setField('gross_weight', e.target.value)} placeholder="0.0" data-testid="product-gross-weight" />
            </Field>
          </div>
        </Section>

        {/* ===== MEDIA ===== */}
        <Section icon={Image} title="Product Media">
          <p className="text-xs text-[#9c9db7] mb-4">Add up to 10 product images and 1 video URL</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {form.images.map((img, i) => (
              <div key={i}>
                <Label className="text-[10px] font-semibold text-[#9c9db7]">Image {i + 1}</Label>
                <Input value={img} onChange={e => setImage(i, e.target.value)} placeholder={`Image ${i + 1} URL`} className="mt-1 text-xs" data-testid={`product-image-${i + 1}`} />
                {img && (
                  <div className="mt-2 rounded-lg border border-[#e5e5ea] overflow-hidden bg-[#f9f9fb]">
                    <img src={img} alt={`Preview ${i + 1}`} className="w-full h-28 object-contain p-1" onError={e => { e.target.parentElement.innerHTML = '<div class="h-28 flex items-center justify-center text-xs text-red-400">Invalid URL</div>'; }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-5">
            <Field label="Product Video URL">
              <Input value={form.product_video} onChange={e => setField('product_video', e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="product-video" />
            </Field>
            {form.product_video && (
              <div className="mt-3 rounded-lg border border-[#e5e5ea] overflow-hidden bg-black" data-testid="video-preview">
                {form.product_video.includes('youtube.com') || form.product_video.includes('youtu.be') ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${form.product_video.includes('youtu.be') ? form.product_video.split('/').pop() : new URLSearchParams(new URL(form.product_video).search).get('v')}`}
                    className="w-full h-56"
                    allowFullScreen
                    title="Product video"
                  />
                ) : (
                  <video src={form.product_video} controls className="w-full h-56 object-contain" />
                )}
              </div>
            )}
          </div>
        </Section>

        {/* ===== CUSTOM ATTRIBUTES (1-21) ===== */}
        <Section icon={ListDashes} title={`Custom Attributes (${customEntries.length}/${MAX_CUSTOM})`}>
          {customEntries.length === 0 ? (
            <p className="text-sm text-[#9c9db7] py-2">No custom attributes. Add up to {MAX_CUSTOM} key-value pairs.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {customEntries.map(([key, val], i) => (
                <div key={key} className="flex items-center gap-2 bg-[#f9f9fb] rounded-lg px-3 py-2">
                  <span className="text-xs font-semibold text-[#6b6b8a] w-40 truncate" title={key}>{key}</span>
                  <Input value={val} onChange={e => setCustom(key, e.target.value)} className="flex-1 h-8 text-sm" data-testid={`custom-attr-${i + 1}`} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCustom(key)} className="text-red-400 h-8 w-8 p-0"><Trash size={14} /></Button>
                </div>
              ))}
            </div>
          )}
          {customEntries.length < MAX_CUSTOM && (
            <div className="flex gap-2 items-end">
              <div className="flex-1 max-w-[200px]">
                <Label className="text-[10px] font-semibold text-[#9c9db7]">Attribute Name</Label>
                <Input value={newAttrKey} onChange={e => setNewAttrKey(e.target.value)} placeholder="e.g. Warranty" className="mt-1 h-8 text-sm" data-testid="new-attr-key" />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                if (newAttrKey.trim() && !form.custom_attributes[newAttrKey.trim()]) {
                  setCustom(newAttrKey.trim(), '');
                  setNewAttrKey('');
                }
              }} className="h-8 text-xs text-[#7d64ff] border-[#7d64ff]/30" data-testid="add-custom-attr-btn">
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
          )}
        </Section>

        {/* ===== VARIANTS (existing products only) ===== */}
        {!isNew && (
          <Section icon={ListDashes} title={`Variants (${variants.length})`}>
            {variants.length > 0 && (
              <div className="border border-[#e5e5ea] rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#e5e5ea] bg-[#f9f9fb]">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">SKU</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Attributes</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Price</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#9c9db7]">Stock</th>
                    <th className="px-4 py-2.5"></th>
                  </tr></thead>
                  <tbody>
                    {variants.map(v => (
                      <tr key={v.variant_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                        <td className="px-4 py-2.5 font-mono text-xs">{v.sku}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(v.attributes || {}).map(([k, val]) => (
                              <span key={k} className="text-[10px] px-1.5 py-0.5 bg-[#f2f0ff] text-[#7d64ff] rounded font-medium">{k}: {val}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-medium">{formatCurrency(v.price)}</td>
                        <td className="px-4 py-2.5">{v.stock}</td>
                        <td className="px-4 py-2.5">
                          <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteVariant(v.variant_id)} data-testid={`delete-variant-${v.variant_id}`}><Trash size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="bg-[#f9f9fb] rounded-xl p-4">
              <p className="text-xs font-semibold text-[#6b6b8a] mb-3">Add Variant</p>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Size" value={variantForm.size} onChange={e => setVariantForm(f => ({ ...f, size: e.target.value }))} className="text-sm" data-testid="variant-size" />
                <Input placeholder="Color" value={variantForm.color} onChange={e => setVariantForm(f => ({ ...f, color: e.target.value }))} className="text-sm" data-testid="variant-color" />
                <Input placeholder="Material" value={variantForm.material} onChange={e => setVariantForm(f => ({ ...f, material: e.target.value }))} className="text-sm" data-testid="variant-material" />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <Input placeholder="SKU (auto)" value={variantForm.sku} onChange={e => setVariantForm(f => ({ ...f, sku: e.target.value }))} className="text-sm font-mono" />
                <Input type="number" placeholder="Price" value={variantForm.price} onChange={e => setVariantForm(f => ({ ...f, price: e.target.value }))} className="text-sm" data-testid="variant-price" />
                <Input type="number" placeholder="MRP" value={variantForm.mrp} onChange={e => setVariantForm(f => ({ ...f, mrp: e.target.value }))} className="text-sm" />
                <Input type="number" placeholder="Stock" value={variantForm.stock} onChange={e => setVariantForm(f => ({ ...f, stock: e.target.value }))} className="text-sm" data-testid="variant-stock" />
              </div>
              <Button type="button" onClick={handleAddVariant} className="mt-3 bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-sm" disabled={!variantForm.size && !variantForm.color && !variantForm.material} data-testid="add-variant-btn">
                <Plus size={14} className="mr-1" /> Add Variant
              </Button>
            </div>
          </Section>
        )}

        {/* ===== BOTTOM ACTIONS ===== */}
        <div className="flex items-center justify-between py-5 border-t border-[#e5e5ea]">
          <Button type="button" variant="ghost" onClick={() => navigate('/products')} className="text-[#6b6b8a]">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white px-8" data-testid="save-product-bottom-btn">
            <FloppyDisk size={16} className="mr-1.5" /> {saving ? 'Saving...' : isNew ? 'Create Product' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
